import { forwardRef, memo } from "react";

import { cn } from "@btnet/utilities-core";

import "./ValueDisplay.less";

interface IValueDisplayProps {
  id?: string;
  /** The value to be displayed in readonly mode. If null, undefined, or "", `--` will be displayed to represent an empty value. */
  value: React.ReactNode | undefined | null;
  "data-testid"?: string;
  style?: React.CSSProperties | undefined;
  displaySize?: "default" | "small" | "large";
  isTextArea?: boolean;
  className?: string;
  tabIndex?: number | undefined;

  onFocus?: (e: React.FocusEvent<unknown>) => void;
}

/**
 * **PREFER USING `displayWithFriendlyDefault` INSTEAD IF POSSIBLE**
 *
 * Friendly default display value to display when an element is considered empty
 */
const FRIENDLY_DEFAULT_DISPLAY_VALUE = "--";
/**
 * Displays a friendly default display value ("--") when the passed ReactNode is null, undefined, or
 * whitespace
 * @param obj
 */
function displayWithFriendlyDefault(obj: React.ReactNode) {
  if (obj === null || obj === undefined) {
    return FRIENDLY_DEFAULT_DISPLAY_VALUE;
  }

  if (typeof obj === "string" && obj.trim() === "") {
    return FRIENDLY_DEFAULT_DISPLAY_VALUE;
  }

  return obj;
}

export const ValueDisplay = memo(
  forwardRef<HTMLDivElement, IValueDisplayProps>((props, ref) => {
    const { value, style, className, ...otherProps } = props;

    const displayValue = displayWithFriendlyDefault(value);

    const CssSandboxName = "value-display";
    const styleClasses = cn(CssSandboxName, className);
    return (
      <div ref={ref} style={style} className={styleClasses} {...otherProps}>
        {displayValue}
      </div>
    );
  }),
);
